import React, { Suspense } from 'react'
import { CircularProgress, Box, Alert } from '@mui/material'
import ErrorBoundary from './ErrorBoundary'

interface SafeComponentProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  loadingText?: string
}

// ``data-testid``: a lazy route that is still resolving looks, to any
// text-based observer, exactly like a page that has finished rendering and
// simply does not contain the string being looked for. The pack surface gates
// judge the product by its visible text, and R018 R2 / R019 R9 both recorded
// the consequence — a gate's "the placeholder is gone" check passed while this
// fallback was on screen, then the real render brought the placeholder back.
// The marker lets a gate wait for a *settled* page instead of guessing, and it
// is locale-proof in a way that matching "로딩 중..." is not.
const DefaultFallback = ({ loadingText = "로딩 중..." }: { loadingText?: string }) => (
  <Box
    data-testid="app-suspense-loading"
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
    flexDirection="column"
    gap={2}
  >
    <CircularProgress />
    <span>{loadingText}</span>
  </Box>
)

const SafeComponent: React.FC<SafeComponentProps> = ({ 
  children, 
  fallback, 
  loadingText 
}) => {
  return (
    <ErrorBoundary>
      <Suspense fallback={fallback || <DefaultFallback loadingText={loadingText} />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

export default SafeComponent